import * as Sentry from '@sentry/nextjs';
import axios from 'axios';

// These are deliberately NOT prefixed with NEXT_PUBLIC_. In Next.js that prefix
// is an instruction to inline the literal value into the browser bundle, which
// is exactly how these WordPress credentials became readable in our shipped
// JavaScript. Without the prefix Next.js exposes them to server code only; in a
// browser they read as undefined, so no credential is ever written into a client
// chunk even if this module gets bundled into one. See GearBoard #1683.
const WORDPRESS_USER = process.env.WORDPRESS_USER || null;
const WORDPRESS_URL = process.env.WORDPRESS_URL || null;
const WORDPRESS_PASS = process.env.WORDPRESS_PASS || null;

// Server-side only. This module can still reach a client bundle through the
// @util/blog barrel, where these values are undefined BY DESIGN — warning there
// would print on every visitor's console for a non-condition.
if (
  typeof window === 'undefined' &&
  (!WORDPRESS_USER || !WORDPRESS_PASS || !WORDPRESS_URL)
) {
  console.warn('Missing WordPress credentials.');
}

// One report per instance, not per request. Set on first use rather than at
// module scope so the alarm cannot fire before Sentry has initialised.
let missingCredentialsReported = false;

interface pageInfo {
  hasNextPage: string | boolean,
  hasPreviousPage:string | boolean,
  endCursor: string,
  startCursor: string
}

export interface WordpressResponse<T = any> {
  [key: string]: {
    pageInfo: pageInfo;
    edges?: T[];
    nodes?: T[];
  };
}

export interface FetchResponse{
  pageInfo: pageInfo | null;
  results: any;
}

export interface CategoryType {
  name: string;
  slug: string;
}

export const fetchFromWordpress = async <T>(
  path: string,
  params?: Record<string, any>
): Promise<FetchResponse | null> => {
  // This helper authenticates to WordPress and must never run in a browser.
  // Client callers go through /api/blog/* instead. Returning null (rather than
  // throwing) preserves the existing degrade-to-empty contract that every caller
  // already relies on, while the console.error makes an accidental client call
  // visible instead of silently rendering an empty section.
  if (typeof window !== 'undefined') {
    console.error(
      'fetchFromWordpress must run server-side. Use an /api/blog/* route.'
    );
    return null;
  }

  if (!WORDPRESS_USER || !WORDPRESS_PASS || !WORDPRESS_URL) {
    // The deploy-ordering failure: code is live, environment is not. This is the
    // single worst outcome this change can produce, and until now it was
    // completely silent — the guard returns before the request, so no request
    // error is ever raised and only a console.warn exists, which nothing pages
    // on (no CaptureConsole integration is configured).
    //
    // It takes the WHOLE blog dark, not just the homepage strip: the index
    // empties and every post URL redirects to /blog. Worse, [...slug].tsx sets
    // its Cache-Control BEFORE calling WordPress, so those redirects are
    // edge-cacheable for up to 20 minutes (s-maxage=300 + swr=900) and outlive
    // the fix.
    //
    // Reported once per instance, not per request, so a misconfiguration pages
    // us without flooding.
    if (!missingCredentialsReported) {
      missingCredentialsReported = true;
      Sentry.captureException(
        new Error(
          'WordPress credentials are not configured — blog content will be unavailable'
        )
      );
    }
    return null;
  }

  const variables = params?.variables ? JSON.stringify(params.variables) : undefined
  const queryParams = {
    ...params,
    variables: variables
  };

  // The try covers ONLY the request. It deliberately does not wrap the
  // unwrapping below, because WordPress answering "no such post" is a normal,
  // designed-for outcome — getBlogBySlug null-checks it into a redirect — and
  // an alarm that fires every time a bot hits a dead blog URL is an alarm
  // nobody reads.
  let response;
  try {
    response = await axios.get<{
      data: WordpressResponse
    }>(`${WORDPRESS_URL}/${path}`, {
      auth: {
        username: WORDPRESS_USER,
        password: WORDPRESS_PASS,
      },
      params: queryParams,
      // Without this, a hung WordPress parks a Vercel invocation until the
      // platform timeout, in a pool shared with checkout.
      timeout: 10_000,
    });
  } catch (err) {
    // A genuine transport or HTTP failure: WordPress is down, unreachable, slow,
    // or rejecting us. THIS is the condition worth paging on. Before this, the
    // catch returned null silently, and because the service layer turns null
    // into an empty array, a total WordPress outage produced a blank blog with
    // no signal anywhere.
    //
    // Report a FRESH error rather than `err`. A measured experiment against
    // @sentry/node 7.120.4 with the default integrations found that the raw
    // axios error's `config.auth` is NOT serialized into the envelope today, so
    // this is defence in depth rather than a leak being actively prevented. It
    // is still worth keeping: `ExtraErrorData` or `LocalVariables` becoming
    // enabled later would change that answer, and WORDPRESS_USER/PASS are
    // locals in this frame.
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    const reason = err instanceof Error ? err.message : 'unknown error';
    Sentry.captureException(
      new Error(
        `WordPress fetch failed (${path}${
          status ? `, HTTP ${status}` : ''
        }): ${reason}`
      )
    );
    return null;
  }

  const wpResponse = response.data?.data;
  const wpData = params?.queryId === "getAllCategories"
    ? wpResponse?.categories
    : params?.queryId === "post"
    ? wpResponse?.post
    : wpResponse?.posts

  // Absent data is "not found", not a failure. Same null the caller already
  // handled before this change; no Sentry event.
  if (!wpData) return null;

  return {
    pageInfo: wpData.pageInfo || {
      hasNextPage: false,
      hasPreviousPage: false,
      endCursor: "",
      startCursor: ""
    },
    results: params?.queryId === "getAllCategories" ? wpData.nodes : params?.queryId === "post" ? wpData : wpData.edges,
  };
};

/**
 * The single accessor for WordPress credentials.
 *
 * NO CALLER YET, and that is deliberate rather than an oversight: the blog
 * dateModified work (GB #1684) needs an ad-hoc GraphQL query that the persisted
 * queries `fetchFromWordpress` wraps cannot express, and it adopts this as a
 * follow-up once #1685 lands. It exists here so that work has one credential
 * path to consume instead of duplicating env reading and Basic Auth, and
 * because a caller that never names the variables cannot be silently broken by
 * a future rename.
 *
 * Safe in a client bundle by construction: without the NEXT_PUBLIC_ prefix
 * Next.js does not write these values into client chunks, so all three
 * constants are null in a browser and this returns null.
 */
export const getWordpressConfig = () =>
  WORDPRESS_USER && WORDPRESS_PASS && WORDPRESS_URL
    ? { url: WORDPRESS_URL, user: WORDPRESS_USER, pass: WORDPRESS_PASS }
    : null;
